import { format, parseISO } from 'date-fns'
import memoize from 'fast-memoize'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import prettyBytes from 'pretty-bytes'

import { IDate } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  shortFormatCurrency,
  shortFormatNumber,
  shortFormatPercentage,
} from '@avantstay/auto-format-number'
import { formatDateRange } from '@avantstay/format-date-range'

import { SHORT_ISO_DATE_FORMAT } from './date'

export const capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1)

const SKIP_CAPITALIZATION = ['of', 'the', 'and', 'for', 'in', 'on', 'at', 'to', 'by', 'with']

export const capitalizeAll = (string: string) =>
  string
    .split(' ')
    .map((part, index) =>
      index > 0 && SKIP_CAPITALIZATION.includes(part.toLowerCase()) ? part : capitalize(part),
    )
    .join(' ')

export const capitalizeEachFirstLetter = (string: string) =>
  string
    .toLowerCase()
    ?.trim()
    ?.split(' ')
    .map(word => {
      return word[0]?.toUpperCase() + word.substring(1)
    })
    .join(' ')

export const formatMoney = (value: any, hideDecimals = false) => {
  if (value === undefined || value === null) return

  if (typeof value === 'number') {
    return (hideDecimals ? wholeDollarFormatter : dollarFormatter).format(value)
  }

  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: hideDecimals ? 0 : undefined,
    minimumFractionDigits: hideDecimals ? 0 : undefined,
  })
}

const dollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const wholeDollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
})

export const formatMoneyCents = (value?: number, onlyInteger = false) => {
  if (value === undefined) return
  return formatMoney(value / 100, onlyInteger)
}

export const formatFileSize = (value: number) => prettyBytes(value)

export const shortFormatMoney = memoize((value: any) =>
  (value && value !== -1) || value === 0 ? shortFormatCurrency(value) : '-',
)

export const formatNumber = memoize((value: any) => {
  return (value && value !== -1) || value === 0 ? shortFormatNumber(value) : '-'
})

export const formatAvgNumber = memoize((value: any) => {
  return (value && value !== -1) || value === 0
    ? new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(value)
    : '-'
})

export const formatPercentage = (value: any, showZero = false, forceSign?: boolean) => {
  const isDisplayedValueZero = Number(value).toFixed(2) === '0.00'
  if ((value === 0 || isDisplayedValueZero) && showZero) return '0%'

  if (!value && value !== 0) return '-'

  return `${forceSign && value > 0 ? '+' : ''}${shortFormatPercentage(value)}`
}

export const formatPercentageReviewOverviews = memoize((value: number, showZero = false) => {
  const isDisplayedValueZero = Number(value).toFixed(2) === '0.00'
  if ((value === 0 || isDisplayedValueZero) && showZero) return '0%'

  if (!value && value !== 0) return '-'

  const formattedValue = Number(value).toLocaleString()
  return `${formattedValue}%`
})

export const formatSignaledPercentage = memoize((value: any) => {
  if (!value && value !== 0) return '-'

  if (value === 0) return shortFormatPercentage(value)

  return `${value > 0 ? '+' : ''}${shortFormatPercentage(value)}`
})

const pluralMapping = {
  one: 'st',
  two: 'nd',
  few: 'rd',
  other: 'th',
}

export const formatOrdinalNumber = (value: number) => {
  const plurals = new Intl.PluralRules('en-US', { type: 'ordinal' })

  const getPlural = plurals.select(value)
  const pluralEnding = pluralMapping[getPlural as keyof typeof pluralMapping]

  return `${value}${pluralEnding}`
}

// parseISODate used to migrate date-fns from v1 to v2
export const parseISODate = (date: Date | string | IDate | number) => {
  if (typeof date === 'string') {
    const dateWithTimeAndZone = date.match(
      /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z?)$/,
    )
    const dateWithTime = date.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})$/)

    if (dateWithTimeAndZone || dateWithTime) {
      return parseISO(date)
    }

    const newDate = new Date(date)
    // We use UTC here since expected date doesn't have specific time and would provide local time
    return new Date(
      newDate.getUTCFullYear(),
      newDate.getUTCMonth(),
      newDate.getUTCDate(),
      newDate.getUTCHours(),
      newDate.getUTCMinutes(),
      newDate.getUTCSeconds(),
      newDate.getUTCMilliseconds(),
    )
  }

  return new Date(date)
}

export const formatDate = (date: any, mask = SHORT_ISO_DATE_FORMAT) => {
  const dateValue = parseISODate(date)

  return format(dateValue, mask)
}

export const numberWithCommas = (number?: string | number) => {
  return String(number).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const formatDateRangeMonthly = (start: Date | string, end: Date | string) => {
  const startDate = parseISODate(start)
  const endDate = parseISODate(end)

  return formatDateRange(startDate, endDate, 'en-US', {
    formats: {
      defaultStart: 'MMM, YYYY',
      defaultEnd: 'MMM, YYYY',
      sameMonthStart: 'MMM',
      sameMonthEnd: ' YYYY',
      sameYearStart: 'MMM ',
      sameYearEnd: 'MMM, YYYY',
    },
  })
}

export const formatAnyPhone = (phone: string) => {
  const clear = phone.replace(/[ \-().]/g, '')
  const full = phone.startsWith('+') ? clear : `+1${clear}`
  const formatted = parsePhoneNumberFromString(full)
  return formatted ? formatted.formatInternational() : full
}

export const formatPhone = (phone: string) => {
  const formatted = parsePhoneNumberFromString(phone)
  return formatted ? formatted.formatInternational() : phone
}

export const formatPhoneUS = (phone: string) => {
  const formatted = parsePhoneNumberFromString(phone)
  return formatted ? formatted.formatNational() : phone
}

export const formatPhoneString = (phone?: string) => {
  if (!phone) {
    return
  }
  return phone.startsWith('+') ? phone : `+${phone}`
}

export const convertDateToTimestamp = (date?: IDate) => {
  if (typeof date === 'string' || typeof date === 'number') {
    return parseISO(date).getTime()
  }
  return date?.getTime()
}

export const formatDateRangeExtraOptions = {
  formats: {
    defaultStart: 'MMM D, YY',
    defaultEnd: 'MMM D, YY',
    sameMonthStart: 'MMM D',
    sameMonthEnd: 'D, YY',
    sameYearStart: 'MMM D',
    sameYearEnd: 'MMM D, YY',
  },
}

export const formatAbbreviatedMoney = (value?: number) => {
  if (!value) return undefined
  const stringNumber = String(value)
  const withoutCents = stringNumber.substring(0, stringNumber.length - 2)
  return shortFormatCurrency(Number(withoutCents))
}

export const combineDateAndTime = (date: string, time: string) => {
  return `${date.split('T')[0]}T${time}`
}
